import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { MapPin } from 'lucide-react';

const CITIES = [
  { id: 'varna', labelKey: 'location_modal.varna' },
  { id: 'sofia', labelKey: 'location_modal.sofia' },
  { id: 'plovdiv', labelKey: 'location_modal.plovdiv' },
  { id: 'burgas', labelKey: 'location_modal.burgas' },
  { id: 'other', labelKey: 'location_modal.other' }
];

export const LocationModal = ({ 
  open, 
  onOpenChange, 
  onSave,
  onSkip 
}) => {
  const { t } = useTranslation();
  const [selectedCity, setSelectedCity] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    // Determine which city value to save
    let cityToSave = selectedCity;
    if (selectedCity === 'other') {
      if (!customCity.trim()) return; // Require custom city if "other" is selected
      cityToSave = 'custom:' + customCity.trim();
    }
    if (!cityToSave) return;
    
    setSaving(true);
    try {
      await onSave(cityToSave);
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving location:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    }
    onOpenChange(false);
  };

  // Check if save button should be disabled
  const isSaveDisabled = !selectedCity || (selectedCity === 'other' && !customCity.trim()) || saving;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="location-modal">
        <DialogHeader>
          <DialogTitle className="text-center text-xl">
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <span>{t('location_modal.title')}</span>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          <p className="text-center text-gray-600">
            {t('location_modal.subtitle')}
          </p>
          
          <Select value={selectedCity} onValueChange={(val) => { setSelectedCity(val); if (val !== 'other') setCustomCity(''); }}>
            <SelectTrigger className="w-full" data-testid="city-select">
              <SelectValue placeholder={t('location_modal.select_placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {CITIES.map((city) => (
                <SelectItem 
                  key={city.id} 
                  value={city.id}
                  data-testid={`city-option-${city.id}`}
                >
                  {t(city.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Custom city input when "other" is selected */}
          {selectedCity === 'other' && (
            <Input
              type="text"
              value={customCity}
              onChange={(e) => setCustomCity(e.target.value)}
              placeholder={t('enterCity') || 'Въведете град...'}
              className="w-full"
              data-testid="custom-city-input-modal"
              autoFocus
            />
          )}
        </div>
        
        <div className="flex gap-3 justify-center">
          <Button 
            variant="ghost" 
            onClick={handleSkip}
            className="text-gray-500 hover:text-gray-700"
            data-testid="location-skip-btn"
          >
            {t('location_modal.skip')}
          </Button>
          <Button 
            onClick={handleSave}
            disabled={isSaveDisabled}
            className="bg-purple-600 hover:bg-purple-700 text-white px-8"
            data-testid="location-continue-btn"
          >
            {saving ? '...' : t('location_modal.continue')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LocationModal;
